/**
 * LineChartCard Component
 * Reusable line chart with Card wrapper for time-series analytics
 *
 * @description Shared line chart for analytics dashboards (admin and provider)
 */

import { TrendingUp } from "lucide-react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import type { ChartConfig } from "../ui/chart";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { cn } from "../ui/cn";

interface LineChartCardProps {
  readonly data: Array<Record<string, unknown>>;
  readonly title: string;
  readonly description?: string;
  readonly valueKey?: string;
  readonly valueLabel?: string;
  readonly nameKey?: string;
  readonly color?: string;
  readonly footerMetric?: string;
  readonly footerValue?: string | number;
  /** Optional className for card container (e.g., h-full for grid layouts) */
  readonly className?: string;
}

export function LineChartCard({
  data,
  title,
  description,
  valueKey = "value",
  valueLabel = "Value",
  nameKey = "name",
  color = "hsl(43, 87%, 60%)", // #F3B33B
  footerMetric,
  footerValue,
  className,
}: LineChartCardProps) {
  if (data.length === 0) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[300px] text-neutral-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  const chartConfig = {
    [valueKey]: {
      label: valueLabel,
      color,
    },
  } satisfies ChartConfig;

  const total = data.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
  const average = Math.round(total / data.length);

  return (
    <Card
      className={cn(
        "border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900/70 transition-colors flex flex-col",
        className,
      )}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-white">{title}</CardTitle>
            {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
          </div>
          {(footerMetric || footerValue) && (
            <div className="flex flex-col items-end gap-1 text-sm pt-2">
              <div className="flex gap-2 font-medium leading-none text-white">
                {footerMetric}: {typeof footerValue === "number" ? footerValue.toLocaleString() : footerValue}
                <TrendingUp className="h-4 w-4 text-lumi-accent" />
              </div>
              <div className="leading-none text-neutral-500">Average: {average.toLocaleString()}</div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-0 -ml-4 pr-4">
        <ChartContainer config={chartConfig} className="h-[300px] w-full !aspect-auto">
          <LineChart data={data} margin={{ top: 20, right: 12, left: 12, bottom: 5 }}>
            <defs>
              <linearGradient id={`gradient-${valueKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-neutral-800" />
            <XAxis dataKey={nameKey} tickLine={false} tickMargin={10} axisLine={false} className="text-neutral-500" />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} className="text-neutral-500" />
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel indicator="line" />} />
            <Line
              type="monotone"
              dataKey={valueKey}
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
